import { useState } from 'react';
import { Mail, Send, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';

// Function to get the API base URL
const getApiBaseUrl = () => {
  // Check if we're in development or production
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    // Development - use the backend port
    return 'https://localhost:7204';
  }
  
  // Production - use your actual API subdomain
  return 'https://api.bildiapp.com';
};


// Function to send dashboard URL via email
const sendDashboardUrl = async (buildingId, email, recipientName, expirationHours = 720) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/api/ExternalDashboard/send-dashboard-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add CORS headers for development
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        buildingId: buildingId,
        email: email,
        recipientName: recipientName,
        expirationHours: expirationHours
      }),
    });

    if (!response.ok) {
      // Get error details from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.errors) {
          errorMessage = Object.values(errorData.errors).flat().join(', ');
        }
      } catch (e) {
        // If we can't parse error, use status text
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// NEW: Function to get building data for external access
const getBuildingDataExternal = async (encryptedBuildingId) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    
    const response = await fetch(`${apiBaseUrl}/api/ExternalDashboard/get-building-data?bid=${encodeURIComponent(encryptedBuildingId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });


    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

const SendDashboardUrlComponent = ({ buildingId, buildingAddress }) => {
  const [email, setEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [expirationDays, setExpirationDays] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success', 'error', null
  const [message, setMessage] = useState('');

  const handleSendUrl = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!email.trim()) {
      setStatus('error');
      setMessage('כתובת אימייל נדרשת');
      return;
    }

    if (!buildingId) {
      setStatus('error');
      setMessage('לא נמצא מזהה בניין');
      return;
    }

    setIsLoading(true);
    setStatus(null);
    setMessage('');

    try {
      const result = await sendDashboardUrl(
        buildingId,
        email.trim(),
        recipientName.trim() || 'מנהל הבניין',
        expirationDays * 24 // Convert days to hours
      );

      if (result.success) {
        setStatus('success');
        setMessage('קישור לוח הבקרה נשלח בהצלחה לאימייל!');
        
        // Clear form after success
        setEmail('');
        setRecipientName('');
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setStatus(null);
          setMessage('');
        }, 5000);
      } else {
        setStatus('error');
        setMessage(result.message || 'שגיאה בשליחת האימייל');
      }
    } catch (error) {
      setStatus('error');
      setMessage(`שגיאה בשליחת האימייל: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
      padding: '1.5rem',
      margin: '1rem 0'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '1rem',
      paddingBottom: '1rem',
      borderBottom: '1px solid #e5e7eb'
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '700',
      margin: 0,
      color: '#1e40af'
    },
    icon: {
      width: '1.5rem',
      height: '1.5rem',
      marginLeft: '0.5rem',
      color: '#1e40af'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontWeight: '600',
      fontSize: '0.875rem',
      color: '#374151'
    },
    input: {
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '1rem',
      direction: 'ltr',
      textAlign: 'left'
    },
    inputRtl: {
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '1rem',
      direction: 'rtl',
      textAlign: 'right'
    },
    select: {
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '1rem',
      backgroundColor: 'white',
      cursor: 'pointer'
    },
    button: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#1e40af',
      color: 'white',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s'
    },
    buttonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed'
    },
    statusMessage: {
      padding: '0.75rem',
      borderRadius: '0.375rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    successMessage: {
      backgroundColor: '#dcfce7',
      color: '#15803d',
      border: '1px solid #86efac'
    },
    errorMessage: {
      backgroundColor: '#fef2f2',
      color: '#dc2626',
      border: '1px solid #fca5a5'
    },
    buildingInfo: {
      backgroundColor: '#f3f4f6',
      padding: '1rem',
      borderRadius: '0.375rem',
      marginBottom: '1rem'
    },
    buildingInfoText: {
      margin: 0,
      color: '#4b5563',
      fontSize: '0.875rem'
    },
    row: {
      gap: '1rem'
    },
    column: {
      flex: 1
    },
    debugInfo: {
      backgroundColor: '#f0f9ff',
      border: '1px solid #0ea5e9',
      borderRadius: '0.375rem',
      padding: '0.75rem',
      fontSize: '0.75rem',
      color: '#0c4a6e',
      marginBottom: '1rem'
    }
  };

  return (
    <div style={styles.container} dir="rtl">
      <div style={styles.header}>
        <Mail style={styles.icon} />
        <h3 style={styles.title}>שליחת קישור לוח בקרה חיצוני</h3>
      </div>

      {/* Debug Info */}
      <div style={styles.debugInfo}>
        <strong>Debug Info:</strong> API Base URL: {getApiBaseUrl()} | Building ID: {buildingId}
      </div>

      {buildingAddress && (
        <div style={styles.buildingInfo}>
          <p style={styles.buildingInfoText}>
            <strong>בניין:</strong> {buildingAddress}
          </p>
        </div>
      )}

      <form style={styles.form} onSubmit={handleSendUrl}>
        <div style={styles.row}>
          <div style={{ ...styles.inputGroup, ...styles.column }}>
            <label style={styles.label}>כתובת אימייל *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@email.com"
              style={styles.input}
              required
              disabled={isLoading}
            />
          </div>
          <div style={{ ...styles.inputGroup, ...styles.column }}>
            <label style={styles.label}>שם הנמען (אופציונלי)</label>
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="מנהל הבניין"
              style={styles.inputRtl}
              disabled={isLoading}
            />
          </div>
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>תקופת תוקף הקישור</label>
          <select
            value={expirationDays}
            onChange={(e) => setExpirationDays(parseInt(e.target.value))}
            style={styles.select}
            disabled={isLoading}
          >
            <option value={1}>יום אחד</option>
            <option value={7}>שבוע</option>
            <option value={30}>חודש (ברירת מחדל)</option>
            <option value={90}>3 חודשים</option>
            <option value={365}>שנה</option>
          </select>
        </div>

        {/* Status Messages */}
        {status === 'success' && (
          <div style={{ ...styles.statusMessage, ...styles.successMessage }}>
            <CheckCircle size={20} />
            <span>{message}</span>
          </div>
        )}

        {status === 'error' && (
          <div style={{ ...styles.statusMessage, ...styles.errorMessage }}>
            <AlertCircle size={20} />
            <span>{message}</span>
          </div>
        )}

        <button
          type="submit"
          style={{
            ...styles.button,
            ...(isLoading ? styles.buttonDisabled : {})
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
              שולח...
            </>
          ) : (
            <>
              <Send size={20} />
              שלח קישור לוח בקרה
            </>
          )}
        </button>
      </form>

      <div style={{
        marginTop: '1rem',
        padding: '1rem',
        backgroundColor: '#fef9c3',
        borderRadius: '0.375rem',
        border: '1px solid #fde047'
      }}>
        <p style={{
          margin: 0,
          fontSize: '0.875rem',
          color: '#a16207'
        }}>
          <strong>הערה:</strong> הקישור מיועד למסכי טלוויזיה חיצוניים ולא דורש כניסה למערכת. 
          הקישור יהיה תקף למשך הזמן שנבחר ויאפשר צפייה בנתוני הבניין בלבד.
        </p>
      </div>
    </div>
  );
};

// Export both the component and the helper function for use in ExtDashboard
export { getBuildingDataExternal };
export default SendDashboardUrlComponent;